import { useEffect, useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'
import { ActivityHistoryModel, ProfileModel, ActivityModel } from 'types'
import PageAppBar from 'components/PageAppBar'
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Typography,
  CircularProgress,
} from '@mui/material'
import axios from 'axios'
import { base_url } from 'env'

// Helper component for rating sliders
const RatingSlider = ({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (event: Event, value: number | number[]) => void
}) => (
  <Box sx={{ width: '100%' }}>
    <Typography gutterBottom>{label}</Typography>
    <Slider
      value={value}
      onChange={onChange}
      step={1}
      marks
      min={1}
      max={5}
      valueLabelDisplay="auto"
    />
  </Box>
)

export default function ActivityHistoryCreationPage() {
  const profile = useLoaderData() as ProfileModel
  const navigate = useNavigate()

  // State for the form data
  const [activityHistory, setActivityHistory] = useState<
    Omit<ActivityHistoryModel, 'id' | 'profileId'>
  >({
    length: 15,
    date: Math.floor(Date.now() / 1000), // Use current date as default Unix timestamp
    giftedPoints: 10,
    ratingDifficulty: 3,
    ratingEnjoyed: 3,
    ratingFocused: 3,
    ratingLength: 3,
    activityId: '',
  })

  // State for the activity dropdown and loading
  const [activities, setActivities] = useState<ActivityModel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) return

    axios
      .get(`${base_url}/activity/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setActivities(res.data)
      })
      .catch((err) => console.error('Failed to load activities', err))
      .finally(() => setIsLoading(false))
  }, [])

  const handleSubmit = () => {
    if (!activityHistory.activityId) {
      alert('Veuillez sélectionner une activité.')
      return
    }
    setIsSaving(true)
    const token = localStorage.getItem('token')

    const payload = {
      ...activityHistory,
      profileId: profile.id,
    }

    axios
      .post(`${base_url}/profile/${profile.id}/activity`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        alert('Activité enregistrée avec succès !')
        navigate(`/profile/${profile.id}`) // Navigate back to profile preview
      })
      .catch((err) => {
        console.error('Failed to save activity history', err)
        alert("L'enregistrement a échoué.")
      })
      .finally(() => setIsSaving(false))
  }

  if (isLoading) {
    return (
      <PageAppBar pageName="Chargement...">
        <CircularProgress />
      </PageAppBar>
    )
  }

  return (
    <PageAppBar pageName={`Enregistrer une activité pour ${profile.firstName}`}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          alignItems: 'center',
          padding: '2rem',
          maxWidth: '600px',
          margin: '0 auto',
        }}
      >
        <Typography variant="h5">Nouvel enregistrement</Typography>

        <FormControl fullWidth required>
          <InputLabel id="activity-select-label">Activité</InputLabel>
          <Select
            labelId="activity-select-label"
            value={activityHistory.activityId}
            label="Activité"
            onChange={(e) =>
              setActivityHistory({
                ...activityHistory,
                activityId: e.target.value,
              })
            }
          >
            {activities.map((act) => (
              <MenuItem key={act.id} value={act.id}>
                {act.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Durée (en minutes)"
          type="number"
          value={activityHistory.length}
          onChange={(e) =>
            setActivityHistory({
              ...activityHistory,
              length: Number(e.target.value),
            })
          }
        />

        <TextField
          fullWidth
          label="Points Obtenus"
          type="number"
          value={activityHistory.giftedPoints}
          onChange={(e) =>
            setActivityHistory({
              ...activityHistory,
              giftedPoints: Number(e.target.value),
            })
          }
        />

        <Typography variant="h6" sx={{ mt: 2 }}>
          Évaluations
        </Typography>

        <RatingSlider
          label="Évaluation de la durée"
          value={activityHistory.ratingLength}
          onChange={(e, v) =>
            setActivityHistory({
              ...activityHistory,
              ratingLength: v as number,
            })
          }
        />
        <RatingSlider
          label="Évaluation de la difficulté"
          value={activityHistory.ratingDifficulty}
          onChange={(e, v) =>
            setActivityHistory({
              ...activityHistory,
              ratingDifficulty: v as number,
            })
          }
        />
        <RatingSlider
          label="Niveau d'appréciation"
          value={activityHistory.ratingEnjoyed}
          onChange={(e, v) =>
            setActivityHistory({
              ...activityHistory,
              ratingEnjoyed: v as number,
            })
          }
        />
        <RatingSlider
          label="Niveau de concentration"
          value={activityHistory.ratingFocused}
          onChange={(e, v) =>
            setActivityHistory({
              ...activityHistory,
              ratingFocused: v as number,
            })
          }
        />

        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={isSaving}
          sx={{ mt: 3, minWidth: 150 }}
        >
          {isSaving ? <CircularProgress size={24} /> : 'Enregistrer'}
        </Button>
      </Box>
    </PageAppBar>
  )
}
